import { ElMessage, ElMessageBox } from "element-plus";

// Element Plus 消息的统一默认配置，方便集中调整样式与交互行为
const DEFAULT_MESSAGE_OPTIONS = {   // 默认消息配置
  duration: 5000,                   // 展示 2.8s
  showClose: true,                  // 显示关闭按钮
  offset: 80,                       // 距离顶部 80px
  grouping: false,                  // 不合并重复消息
  customClass: "ew-message",        // 自定义样式类
}
function normalizeConfig(message, options = {}) {
  if (typeof message === "string") {
    return { message, ...options };
  }
  if (message && typeof message === "object") {
    return { ...message, ...options };
  }
  return options;
}

function dispatchMessage(payload, options) {
  if (!payload) return null;
  const config = normalizeConfig(payload, options);
  return ElMessage({
    ...DEFAULT_MESSAGE_OPTIONS,
    ...config,
  });
}

const Message = {
  show(message, options) {
    return dispatchMessage(message, options);
  },
  success(message, options) {
    return dispatchMessage(message, { type: "success", ...options });
  },
  warning(message, options) {
    return dispatchMessage(message, { type: "warning", ...options });
  },
  /**
   * 显示错误消息
   * @param {string|object} message - 错误消息
   * @param {object|Error} optionsOrError - 配置选项或 Error 对象
   * @returns {object} ElMessage 实例
   *
   * @example
   * // 只显示消息
   * Message.error('操作失败')
   *
   * // 显示消息并打印错误到控制台
   * Message.error('操作失败', error)
   *
   * // 显示消息并打印错误到控制台，带自定义配置
   * Message.error('操作失败', { error, duration: 3000 })
   */
  error(message, optionsOrError) {
    let options = {}
    let error = null

    // 如果第二个参数是 Error 对象，自动打印到控制台
    if (optionsOrError instanceof Error) {
      error = optionsOrError
      console.error(message + ':', error)
    }
    // 如果第二个参数是对象，检查是否有 error 字段
    else if (optionsOrError && typeof optionsOrError === 'object') {
      options = optionsOrError
      if (options.error instanceof Error) {
        error = options.error
        console.error(message + ':', error)
        // 从 options 中移除 error，避免传递给 ElMessage
        const { error: _, ...restOptions } = options
        options = restOptions
      }
    }

    return dispatchMessage(message, { type: "error", ...options });
  },
  /**
   * 弹窗提示
   * @param {string} message
   * @param {string} [title]
   * @param {object} [options]
   */
  alert(message, title = '提示', options = {}) {
    const defaultOptions = {
      confirmButtonText: '知道了',
      type: 'error',
      dangerouslyUseHTMLString: false,
      ...options,
    }
    return ElMessageBox.alert(message, title, defaultOptions)
  },
  info(message, options) {
    return dispatchMessage(message, { type: "info", ...options });
  },
  /**
   * 显示确认对话框
   * @param {string} message - 提示消息
   * @param {string} title - 标题
   * @param {object} options - 配置选项
   * @returns {Promise} Promise 对象，resolve 表示确认，reject 表示取消
   *
   * @example
   * // 基本用法
   * try {
   *   await Message.confirm('确定要删除吗？', '确认删除')
   *   // 用户确认后的操作
   * } catch {
   *   // 用户取消
   * }
   *
   * // 带自定义配置
   * await Message.confirm('确定要删除吗？', '确认删除', {
   *   confirmButtonText: '确定删除',
   *   cancelButtonText: '取消',
   *   type: 'warning'
   * })
   */
  confirm(message, title = '提示', options = {}) {
    // 默认配置：统一中文按钮 + 弹框 top 位置
    const defaultOptions = {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      customStyle: {
        top: '-30%'
      },
      ...options,
      // 合并 customStyle，确保用户传入的样式不会完全覆盖默认样式
      customStyle: {
        top: '-30%',
        ...(options.customStyle || {})
      }
    }
    return ElMessageBox.confirm(message, title, defaultOptions);
  },
};

export function createMessage(message, options) {
  return dispatchMessage(message, options);
}

export default Message;
